import { SiteUrls } from '@tableau/shared-urls';
import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';

import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '~commonInfra/services/HelpService';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { delayPromise } from '~commonInfra/utils/Delay';
import { Location } from '~commonInfra/utils/Location';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';

import { Messages } from '../../../codegen/Localize';
import { RestApiErrorResponse } from '../../../httpClient/RestApiService';
import { DatabaseForUngrouping, UngroupDatabasesAction } from '../../actions/databases/UngroupDatabasesAction';
import { UngroupDatabasesDialogBody, UngroupDatabasesDialogBodyProps, UngroupDatabasesDialogState } from '../../components/databases/UngroupDatabasesDialog';
import { callViaApiService, UngroupDatabasesRequestConfigParams, UngroupDatabasesResponse } from '../../restApi/databases/UngroupDatabases';

export function* handleUngroupDatabases(action: UngroupDatabasesAction): SagaIterator {

  try {
    yield call(openDialog, action.database);
  } catch {
    return;
  }

  const siteUrls: SiteUrls = yield select(siteUrlsSelector);
  const onPlacePage = yield call(isOnPlacePageOfTargetItem, action.database, siteUrls);

  if (onPlacePage) {
    yield call(redirectToDatabasesList, siteUrls);
  } else {
    // If on the list view, refresh the list
    yield put(requestSpaceContents());
  }
}

// TODO: Can we modify this to use for databases and tables in reloadSpaceIfUpdatingCurrentSpace?
// Doing so could remove the need to do a full refresh after every Catalog action.
export function isOnPlacePageOfTargetItem(database: DatabaseForUngrouping, siteUrls: SiteUrls): boolean {
  // The "Delete Project" saga figures out if we're on the place page by using the SpaceInfo,
  // but the Catalog place pages don't populate SpaceInfo, so we compare the URL directly.
  const currentUrlPath = Location.instance.hash().slice(1);
  const pathOfDatabase = siteUrls.catalogDatabasePath(database.id);
  return currentUrlPath.startsWith(pathOfDatabase);
}

export function redirectToDatabasesList(siteUrls: SiteUrls) {
  // Keep the success toasts visible after navigating away, so the user knows what happened
  const toaster: ToasterService = ToasterService.instance;
  toaster.markToastsAsDoNotDismissOnLocationChange();
  Location.instance.setHash('#' + siteUrls.catalogDatabasesPath());
}

export function openDialog(database: DatabaseForUngrouping) {
  const modalInstance = openReactActionDialog<UngroupDatabasesDialogBodyProps, UngroupDatabasesDialogState, UngroupDatabasesResponse>({
    title: Messages.ungroupDatabases_dialogTitle(),
    bodyComponent: UngroupDatabasesDialogBody,
    initialBodyProps: { database },
    initialDialogState: { copyMetadataToUngroupedItems: true },
    actionName: Messages.ungroupDatabases_submit(),
    action: makeApiCallAndHandleResult,
    dialogWidth: TabDialogWidth.width500px,
    dismissDialogOnActionFailed: false,
  });

  return modalInstance.result;
}

export async function makeApiCallAndHandleResult(props: UngroupDatabasesDialogBodyProps, dialogState: UngroupDatabasesDialogState): Promise<UngroupDatabasesResponse> {
  const params: UngroupDatabasesRequestConfigParams = {
    groupedDatabaseLuid: props.database.luid,
    copyMetadataToUngroupedItems: dialogState.copyMetadataToUngroupedItems,
  };

  const toaster = ToasterService.instance;
  const learnMoreToastUrl = HelpService.getServerHelpUrlByCategory('group-ungroup-timing');

  try {
    const result = await callViaApiService(params);
    await delayPromise(2000); // Wait 2 seconds to allow the result to propagate in the backend
    toaster.pop({
      type: ToastType.Info,
      text: Messages.ungroupDatabases_toastSuccess(),
      actionMessage: Messages.groupDatabases_learnMore(),
      action: () => { window.open(learnMoreToastUrl, '_blank'); }
    });
    return result;

  } catch (e) {
    const errorResponse = e as RestApiErrorResponse;
    toaster.pop({
      type: ToastType.Error,
      text: errorResponse.data.error.detail,
    });
    throw errorResponse;
  }
}
